import { useEffect, useRef, useState } from 'react';

const DEFAULT_MAX_PROGRESS = 99;
const DEFAULT_INTERVAL_MS = 100;

type StepsConfig<T extends string> = Record<T, number>;

type UseSimulatedProgressParams<T extends string> = {
  steps: StepsConfig<T>;
  defaultStep: T;
  duration: number;
  isActive: boolean;
  maxProgress?: number;
  intervalMs?: number;
};

type UseSimulatedProgressReturn<T extends string> = {
  progress: number;
  currentStep: T;
};

/**
 * Ease-out quadratic function for smooth deceleration
 * See https://easings.net/?#easeOutQuad
 */
const easeOutQuad = (t: number): number => t * (2 - t);

/**
 * Get step based on current progress percentage.
 */
const getStepFromProgress = <T extends string>(progressValue: number, steps: StepsConfig<T>, defaultStep: T): T => {
  const entries = Object.entries(steps) as [T, number][];
  const sortedSteps = entries.sort((a, b) => b[1] - a[1]); // Sort descending by threshold
  const match = sortedSteps.find(([, threshold]) => progressValue >= threshold);

  return match ? match[0] : defaultStep;
};

/**
 * Hook to simulate progress through configurable steps.
 * Progress uses easeOutQuad for smooth deceleration.
 * Stalls at maxProgress (default 99%) if isActive remains true beyond duration.
 * When isActive becomes false, progress jumps to 100%.
 *
 * @param steps - Object mapping step names to their progress thresholds
 * @param defaultStep - Initial step when progress starts
 * @param duration - Total duration in ms for the simulated progress
 * @param isActive - Whether progress simulation is active
 * @param maxProgress - Maximum progress before stalling (default 99)
 * @param intervalMs - Update interval in ms (default 100)
 */
export const useSimulatedProgress = <T extends string>({
  steps,
  defaultStep,
  duration,
  isActive,
  maxProgress = DEFAULT_MAX_PROGRESS,
  intervalMs = DEFAULT_INTERVAL_MS,
}: UseSimulatedProgressParams<T>): UseSimulatedProgressReturn<T> => {
  const intervalRef = useRef<number>();
  const startTimeRef = useRef<number>(Date.now());
  const [currentStep, setCurrentStep] = useState<T>(defaultStep);
  const [progress, setProgress] = useState<number>(0);

  useEffect(() => {
    if (isActive) {
      startTimeRef.current = Date.now();
      setProgress(0);
      setCurrentStep(defaultStep);

      intervalRef.current = window.setInterval(() => {
        const elapsedMs = Date.now() - startTimeRef.current;
        const rawProgress = Math.min(elapsedMs / duration, 1);
        const easedProgress = easeOutQuad(rawProgress) * maxProgress;
        const newProgress = Math.min(Math.round(easedProgress), maxProgress);

        setProgress(newProgress);
        setCurrentStep(getStepFromProgress(newProgress, steps, defaultStep));

        // Stop interval when we reach max progress
        if (newProgress >= maxProgress) {
          clearInterval(intervalRef.current);
        }
      }, intervalMs);
    }

    // When processing completes, jump to 100%
    if (!isActive && progress > 0) {
      clearInterval(intervalRef.current);
      setProgress(100);
    }

    return () => clearInterval(intervalRef.current);
  }, [isActive]);

  return { progress, currentStep };
};
